/**
 * Web Audio synthesizer for the ahwa table — no external audio files.
 *
 * Everything is procedural: bone-on-wood clacks (noise burst + body thud),
 * the shuffle rattle (dense randomized micro-clacks), deal flicks, pass
 * knocks, and a Karplus–Strong plucked-string "oud" for celebrations.
 * Audio unlocks on the first user gesture (browser autoplay policy).
 */

type Pan = number; // -1 left .. 1 right

const STORAGE_KEY = "domina.sound.v1";

class SoundManager {
  public enabled = true;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private pluckCache = new Map<string, AudioBuffer>();

  constructor() {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved !== null) this.enabled = saved === "on";
    }
  }

  /** Lazily create/resume the context; call from any user gesture. */
  private ac(): AudioContext | null {
    if (typeof window === "undefined") return null;
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -18;
      comp.knee.value = 24;
      comp.ratio.value = 6;
      const gain = this.ctx.createGain();
      gain.gain.value = 0.9;
      gain.connect(comp);
      comp.connect(this.ctx.destination);
      this.master = gain;
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  /** Call once from a real user gesture so mobile Safari unlocks audio. */
  unlock(): void {
    this.ac();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
    }
  }

  private out(pan: Pan = 0): AudioNode | null {
    const ctx = this.ac();
    if (!ctx || !this.master || !this.enabled) return null;
    if (pan === 0) return this.master;
    const panner = ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    panner.connect(this.master);
    return panner;
  }

  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noise) return this.noise;
    const len = ctx.sampleRate * 0.5;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buf;
    return buf;
  }

  // ------------------------------------------------------------------ core
  // One bone micro-clack: filtered noise tick + short body resonance.
  private tick(opts: { t: number; freq?: number; intensity?: number; pan?: Pan; thud?: boolean }): void {
    const ctx = this.ac();
    const dest = this.out(opts.pan);
    if (!ctx || !dest) return;
    const t = opts.t;
    const intensity = opts.intensity ?? 1;
    const freq = opts.freq ?? 1800 + Math.random() * 1600;

    // Ceramic click: sharp bandpassed noise.
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx);
    src.playbackRate.value = 0.9 + Math.random() * 0.4;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = freq;
    bp.Q.value = 1.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.5 * intensity, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    src.connect(bp);
    bp.connect(g);
    g.connect(dest);
    src.start(t, Math.random() * 0.2, 0.08);

    // Woody body thud under the click.
    if (opts.thud !== false) {
      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = "sine";
      const base = 140 + Math.random() * 60;
      osc.frequency.setValueAtTime(base, t);
      osc.frequency.exponentialRampToValueAtTime(46, t + 0.09);
      og.gain.setValueAtTime(0.55 * intensity, t);
      og.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
      osc.connect(og);
      og.connect(dest);
      osc.start(t);
      osc.stop(t + 0.12);
    }
  }

  // ------------------------------------------------------------- game audio

  /** Bone tile slapping onto the wood. `pan` from screen x, intensity 0..1. */
  place(pan = 0, intensity = 0.9): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    this.tick({ t: ctx.currentTime, intensity, pan });
  }

  /** Heavy double slam — lower body, plus a tiny secondary rattle. */
  slam(pan = 0): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    const t = ctx.currentTime;
    this.tick({ t, intensity: 1.25, freq: 1200 + Math.random() * 500, pan });
    this.tick({ t: t + 0.045, intensity: 0.4, freq: 2600, pan });
    const dest = this.out(pan);
    if (dest) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(95, t);
      osc.frequency.exponentialRampToValueAtTime(34, t + 0.16);
      g.gain.setValueAtTime(0.85, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.connect(g);
      g.connect(dest);
      osc.start(t);
      osc.stop(t + 0.22);
    }
  }

  /** Tiles poured into two hands and riffled together — the ahwa shuffle. */
  shuffle(durationMs = 1500): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    const t0 = ctx.currentTime + 0.02;
    const ticks = Math.floor(durationMs / 55);
    for (let i = 0; i < ticks; i++) {
      // Dense early, settle slightly towards the end.
      const p = i / ticks;
      const jitter = (Math.random() - 0.5) * 0.04;
      this.tick({
        t: t0 + i * 0.055 + jitter,
        freq: 1400 + Math.random() * 2400,
        intensity: (0.16 + Math.random() * 0.2) * (1.05 - 0.3 * p),
        pan: Math.random() * 1.4 - 0.7,
        thud: Math.random() < 0.3,
      });
    }
  }

  /** Single flick — one tile travelling from the pile to a hand. */
  dealFlick(pan = 0): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    const t = ctx.currentTime;
    const dest = this.out(pan);
    if (!dest) return;
    // Air swish.
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(900, t);
    bp.frequency.exponentialRampToValueAtTime(3400, t + 0.1);
    bp.Q.value = 0.9;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.14, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
    src.connect(bp);
    bp.connect(g);
    g.connect(dest);
    src.start(t, Math.random() * 0.2, 0.14);
    // Soft landing tap.
    this.tick({ t: t + 0.1, intensity: 0.32, pan, thud: false });
  }

  /** Lifting a tile off the rack. */
  pickup(pan = 0): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    const dest = this.out(pan);
    if (!ctx || !dest) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1800;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
    src.connect(hp);
    hp.connect(g);
    g.connect(dest);
    src.start(t, Math.random() * 0.3, 0.09);
  }

  /** Drawing from el-mashoob: slide out, then tap into the kaf. */
  draw(pan = 0): void {
    this.dealFlick(pan);
  }

  /** Knocking the table to pass the turn. */
  knock(pan = 0): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    const t = ctx.currentTime;
    this.tick({ t, intensity: 0.55, freq: 700 + Math.random() * 200, pan });
    this.tick({ t: t + 0.14, intensity: 0.45, freq: 640, pan, thud: false });
  }

  /** Hand flip — tiles turning face-up. */
  flip(pan = 0): void {
    this.tick({ t: 0, intensity: 0.35, freq: 2400, pan, thud: false });
  }

  /** Soft UI tick for buttons/toggles. */
  ui(): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    const dest = this.out(0);
    if (!ctx || !dest) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(660, t);
    g.gain.setValueAtTime(0.08, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + 0.07);
  }

  /** Gentle plucked note when your turn starts. */
  turnPing(): void {
    this.pluck(523.25, 0.16, 0);
  }

  // ------------------------------------------------------- Karplus–Strong
  /** Plucked-string note (oud-ish). Cached per frequency. */
  private pluckBuffer(ctx: AudioContext, freq: number): AudioBuffer {
    const key = String(Math.round(freq));
    const cached = this.pluckCache.get(key);
    if (cached) return cached;
    const sr = ctx.sampleRate;
    const dur = 1.1;
    const len = Math.floor(sr * dur);
    const buf = ctx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    const period = Math.max(2, Math.round(sr / freq));
    // Seed with noise, then feedback averaging (Karplus–Strong).
    const delay = new Float32Array(period);
    for (let i = 0; i < period; i++) delay[i] = Math.random() * 2 - 1;
    let idx = 0;
    const damp = 0.996;
    for (let i = 0; i < len; i++) {
      const next = (idx + 1) % period;
      const val = damp * 0.5 * (delay[idx] + delay[next]);
      data[i] = delay[idx];
      delay[idx] = val;
      idx = next;
    }
    // Fade the tail.
    const fade = Math.floor(sr * 0.15);
    for (let i = 0; i < fade; i++) data[len - 1 - i] *= i / fade;
    this.pluckCache.set(key, buf);
    return buf;
  }

  pluck(freq: number, gainValue = 0.2, pan: Pan = 0): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    const dest = this.out(pan);
    if (!ctx || !dest) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.pluckBuffer(ctx, freq);
    const g = ctx.createGain();
    g.gain.value = gainValue;
    src.connect(g);
    g.connect(dest);
    src.start(t);
  }

  /** El-Dabsha! — winning slam: pentatonic oud run over drum thumps. */
  fanfare(): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    // Table drum thumps (dum-tak).
    const drum = (t: number, f: number, v: number) => {
      const dest = this.out(0);
      if (!dest) return;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, t);
      osc.frequency.exponentialRampToValueAtTime(f * 0.4, t + 0.18);
      g.gain.setValueAtTime(v, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.connect(g);
      g.connect(dest);
      osc.start(t);
      osc.stop(t + 0.22);
    };
    drum(t0, 150, 0.5);
    drum(t0 + 0.18, 210, 0.32);
    // Plucked run (nahawand-ish flavour).
    const notes = [392, 466.16, 523.25, 587.33, 698.46, 932.33];
    notes.forEach((f, i) => this.pluck(f, 0.22, i * 0.14 - 0.35));
    setTimeout(() => {
      drum(0, 150, 0) /* noop keeps types honest */;
    }, 0);
    drum(t0 + 0.36, 150, 0.4);
    drum(t0 + 0.54, 210, 0.3);
  }

  /** Qafla — the lock: two dull thuds and a muted rattle. */
  block(): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    if (!ctx) return;
    const t = ctx.currentTime;
    this.tick({ t, intensity: 0.5, freq: 420 });
    this.tick({ t: t + 0.16, intensity: 0.35, freq: 380 });
  }

  /** Match lost — descending minor phrase. */
  lose(): void {
    if (!this.enabled) return;
    const notes = [440, 392, 349.23, 293.66];
    notes.forEach((f, i) => this.pluck(f, 0.2, (i % 2) * 0.3 - 0.15));
  }

  /** Pip count-up ticks on the round-over panel. */
  counterTick(i: number): void {
    if (!this.enabled) return;
    const ctx = this.ac();
    const dest = this.out(0);
    if (!ctx || !dest) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 300 + (i % 4) * 60;
    g.gain.setValueAtTime(0.04, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    osc.connect(g);
    g.connect(dest);
    osc.start(t);
    osc.stop(t + 0.06);
  }
}

export const sounds = new SoundManager();

/** Small haptic kick on supported devices. */
export function haptic(ms = 12): void {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    try {
      navigator.vibrate(ms);
    } catch {
      // ignore
    }
  }
}
